import {
  Alert,
  Button,
  cn,
  Input,
  Modal,
  Select,
  Stepper,
  toast,
  Toggle,
} from "@gotts.ai/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatUnits, parseUnits } from "viem";

import { AmountInput } from "../../components/AmountInput";
import { DecayCurve } from "../../components/DecayCurve";
import { PcbCard } from "../../components/PcbCard";
import { TokenIcon } from "../../components/TokenIcon";
import { TokenSelector } from "../../components/TokenSelector";
import { useDeployment, useWallet } from "../../context";
import { useTokenBalances } from "../../hooks/useTokenBalances";
import { useTransactionFlow } from "../../hooks/useTransactionFlow";
import { useTxFeedback } from "../../hooks/useTxFeedback";
import { MOCK_ERC20_ABI } from "../../lib/abis";
import {
  DEVENV_TOKEN_PRICES,
  MAX_UINT256,
  TOKEN_DECIMALS,
  TOKEN_SYMBOLS,
} from "../../lib/constants";
import { formatTokenDisplay, formatUsd } from "../../lib/format";

import {
  DURATION_PRESETS,
  formatDuration,
  REACTOR_DESCRIPTIONS,
  REACTOR_SUBTITLES,
} from "./types";

import type { StepDefinition } from "../../hooks/useTransactionFlow";
import type { TokenSymbol } from "../../lib/constants";
import type { DutchOrder } from "./types";
import type { Address } from "viem";

let orderCounter = 0;

interface DutchOrderFormProps {
  onOrderCreated: (order: DutchOrder) => void;
  prefill?: Partial<DutchOrder> | null;
  howItWorksDismissed?: boolean;
  onDismissHowItWorks?: () => void;
}

export function DutchOrderForm({
  onOrderCreated,
  prefill,
  howItWorksDismissed,
  onDismissHowItWorks,
}: DutchOrderFormProps) {
  const { deployment } = useDeployment();
  const { walletClient, address, publicClient, mode, chain } = useWallet();
  const { balances } = useTokenBalances();
  const flow = useTransactionFlow();
  const { feedbackClass, onTxSuccess, onTxError } = useTxFeedback();

  const [tokenIn, setTokenIn] = useState<TokenSymbol>("USDC");
  const [tokenOut, setTokenOut] = useState<TokenSymbol>("WETH");
  const [amountIn, setAmountIn] = useState("");
  const [minOutput, setMinOutput] = useState("");
  const [reactor, setReactor] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Dutch auction fields
  const [startAmount, setStartAmount] = useState("");
  const [endAmount, setEndAmount] = useState("");
  const [decayDuration, setDecayDuration] = useState("300");
  const [deadlineMinutes, setDeadlineMinutes] = useState("10"); // 10 min after decay ends

  // Confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [riskAcknowledged, setRiskAcknowledged] = useState(false);

  // Reset risk checkbox when modal opens
  useEffect(() => {
    if (showConfirm) setRiskAcknowledged(false);
  }, [showConfirm]);

  // How it works — use parent prop if provided, fall back to localStorage
  const [internalShowHowItWorks, setInternalShowHowItWorks] = useState(() => {
    try {
      return localStorage.getItem("devenv:uniswapx-how-it-works") !== "hidden";
    } catch {
      return true;
    }
  });
  const showHowItWorks =
    howItWorksDismissed !== undefined
      ? !howItWorksDismissed
      : internalShowHowItWorks;
  const toggleHowItWorks = useCallback(
    (show: boolean) => {
      setInternalShowHowItWorks(show);
      if (onDismissHowItWorks && !show) onDismissHowItWorks();
      try {
        localStorage.setItem(
          "devenv:uniswapx-how-it-works",
          show ? "visible" : "hidden",
        );
      } catch {
        // ignore
      }
    },
    [onDismissHowItWorks],
  );

  // Prefill from duplicated order
  useEffect(() => {
    if (!prefill) return;
    if (prefill.inputToken) setTokenIn(prefill.inputToken);
    if (prefill.outputToken) setTokenOut(prefill.outputToken);
    if (prefill.inputAmount) setAmountIn(prefill.inputAmount);
    if (prefill.minOutput) setMinOutput(prefill.minOutput);
    if (prefill.startAmount) setStartAmount(prefill.startAmount);
    if (prefill.endAmount) setEndAmount(prefill.endAmount);
  }, [prefill]);

  const isConnected = mode !== "disconnected" && !!walletClient && !!address;

  function getTokenAddress(symbol: TokenSymbol): Address | null {
    if (!deployment?.tokens) return null;
    const key = symbol.toLowerCase();
    const tokens = deployment.tokens as unknown as Record<string, Address>;
    return tokens[key] ?? null;
  }

  // Filter reactor options to only those deployed on the current chain
  const reactorOptions = useMemo(() => {
    if (!deployment?.uniswapX) return [];
    const uniswapX = deployment.uniswapX as unknown as Record<string, Address>;
    return Object.entries(REACTOR_DESCRIPTIONS)
      .filter(([key]) => {
        const addr = uniswapX[key];
        // Exclude orderQuoter (quote-only, doesn't execute orders)
        return addr && addr !== "0x" && key !== "orderQuoter";
      })
      .map(([key, label]) => ({
        label,
        value: key,
        description: REACTOR_SUBTITLES[key] ?? "",
      }));
  }, [deployment]);

  // Auto-select first available reactor when deployment loads or chain changes
  useEffect(() => {
    const first = reactorOptions[0];
    if (first && !reactorOptions.some((o) => o.value === reactor)) {
      setReactor(first.value);
    }
  }, [reactorOptions, reactor]);

  // Computed values
  const inputUsd = useMemo(() => {
    const amt = parseFloat(amountIn) || 0;
    const price = DEVENV_TOKEN_PRICES[tokenIn] ?? 1;
    return amt * price;
  }, [amountIn, tokenIn]);

  const minOutputUsd = useMemo(() => {
    const amt = parseFloat(minOutput) || 0;
    const price = DEVENV_TOKEN_PRICES[tokenOut] ?? 1;
    return amt * price;
  }, [minOutput, tokenOut]);

  const effectiveRate = useMemo(() => {
    const inAmt = parseFloat(amountIn);
    const outAmt = parseFloat(minOutput);
    if (!inAmt || !outAmt || inAmt === 0) return null;
    return outAmt / inAmt;
  }, [amountIn, minOutput]);

  // Market rate from mock prices
  const marketRate = useMemo(() => {
    const priceIn = DEVENV_TOKEN_PRICES[tokenIn] ?? 1;
    const priceOut = DEVENV_TOKEN_PRICES[tokenOut] ?? 1;
    if (priceOut === 0) return null;
    return priceIn / priceOut;
  }, [tokenIn, tokenOut]);

  // Auto-suggest start/end amounts based on minOutput
  const suggestedStart = useMemo(() => {
    const min = parseFloat(minOutput);
    if (!min || min <= 0) return null;
    return (min * 1.05).toFixed(6);
  }, [minOutput]);

  const suggestedEnd = useMemo(() => {
    const min = parseFloat(minOutput);
    if (!min || min <= 0) return null;
    return (min * 0.95).toFixed(6);
  }, [minOutput]);

  const startAmountNum = parseFloat(startAmount) || 0;
  const endAmountNum = parseFloat(endAmount) || 0;
  const decayDurationNum = parseInt(decayDuration) || 300;
  const hasDecayParams = startAmountNum > 0 && endAmountNum > 0;

  // Token balances
  const inputBalance = balances[tokenIn];
  const outputBalance = balances[tokenOut];

  // Token selector data
  const tokenBalancesForSelector = useMemo(() => {
    const result: Record<string, { formatted: string }> = {};
    for (const sym of TOKEN_SYMBOLS) {
      const b = balances[sym];
      if (b) {
        result[sym] = {
          formatted: parseFloat(formatUnits(b.raw, b.decimals)).toLocaleString(
            undefined,
            { maximumFractionDigits: 4 },
          ),
        };
      }
    }
    return result;
  }, [balances]);

  const tokenAddressMap = useMemo(() => {
    if (!deployment?.tokens) return {};
    const tokens = deployment.tokens as unknown as Record<string, Address>;
    const result: Record<string, Address> = {};
    for (const sym of TOKEN_SYMBOLS) {
      const addr = tokens[sym.toLowerCase()];
      if (addr) result[sym.toLowerCase()] = addr;
    }
    return result;
  }, [deployment]);

  // Insufficient balance validation
  const insufficientBalance = useMemo(() => {
    if (!amountIn || parseFloat(amountIn) <= 0 || !inputBalance) return false;
    try {
      const decimals = TOKEN_DECIMALS[tokenIn] ?? 18;
      const parsed = parseUnits(amountIn, decimals);
      return parsed > inputBalance.raw;
    } catch {
      return false;
    }
  }, [amountIn, tokenIn, inputBalance]);

  // Critical rate deviation (>50%) for confirmation modal
  const hasCriticalDeviation = useMemo(() => {
    const startAmt = parseFloat(startAmount || amountIn);
    const inAmt = parseFloat(amountIn);
    if (!marketRate || startAmt <= 0 || inAmt <= 0) return false;
    const impliedRate = startAmt / inAmt;
    return Math.abs(impliedRate - marketRate) / marketRate > 0.5;
  }, [startAmount, amountIn, marketRate]);

  const handleSwapTokens = useCallback(() => {
    const prevIn = tokenIn;
    const prevOut = tokenOut;
    setTokenIn(prevOut);
    setTokenOut(prevIn);
  }, [tokenIn, tokenOut]);

  const handleDurationPreset = useCallback((seconds: number) => {
    setDecayDuration(String(seconds));
  }, []);

  function resetForm() {
    setAmountIn("");
    setMinOutput("");
    setStartAmount("");
    setEndAmount("");
    setDecayDuration("300");
    setDeadlineMinutes("10");
    flow.reset();
  }

  async function handleSubmitOrder() {
    if (!walletClient || !address || !deployment) return;
    setShowConfirm(false);

    const tokenInAddr = getTokenAddress(tokenIn);
    if (!tokenInAddr) return;

    const decimals = TOKEN_DECIMALS[tokenIn] ?? 18;
    const parsedAmount = parseUnits(amountIn, decimals);
    const permit2 = deployment.foundation.permit2;

    const now = Math.floor(Date.now() / 1000);
    const duration = parseInt(decayDuration) || 300;
    const deadlineExtra = (parseInt(deadlineMinutes) || 10) * 60;
    const decayStart = now;
    const decayEnd = now + duration;
    const orderDeadline = decayEnd + deadlineExtra;

    const steps: StepDefinition[] = [];

    // Step 1: Approve input token to Permit2
    steps.push({
      label: "Approve to Permit2",
      execute: async () =>
        walletClient.writeContract({
          chain,
          account: address,
          address: tokenInAddr,
          abi: MOCK_ERC20_ABI,
          functionName: "approve",
          args: [permit2, MAX_UINT256],
        }),
      skip: async () => {
        const allowance = await publicClient.readContract({
          address: tokenInAddr,
          abi: MOCK_ERC20_ABI,
          functionName: "allowance",
          args: [address, permit2],
        });
        return allowance >= parsedAmount;
      },
    });

    // Step 2: Sign EIP-712 Dutch auction order
    if (!(reactor in deployment.uniswapX)) {
      toast.error(
        `Reactor "${reactor}" is not deployed on this chain. Try a different reactor or use from-scratch mode.`,
      );
      return;
    }
    const reactorAddress =
      deployment.uniswapX[reactor as keyof typeof deployment.uniswapX];

    steps.push({
      label: "Sign Order",
      execute: async () => {
        const dutchTypes = {
          DutchOrder: [
            { name: "maker", type: "address" },
            { name: "inputToken", type: "address" },
            { name: "inputAmount", type: "uint256" },
            { name: "outputToken", type: "address" },
            { name: "decayStartTime", type: "uint256" },
            { name: "decayEndTime", type: "uint256" },
            { name: "startAmount", type: "uint256" },
            { name: "endAmount", type: "uint256" },
          ],
        } as const;

        const signature: `0x${string}` = await (
          walletClient.signTypedData as (
            args: unknown,
          ) => Promise<`0x${string}`>
        )({
          domain: {
            name: "UniswapX",
            chainId: chain.id,
            verifyingContract: reactorAddress,
          },
          types: dutchTypes,
          primaryType: "DutchOrder",
          message: {
            maker: address,
            inputToken: tokenInAddr,
            inputAmount: parsedAmount,
            outputToken: getTokenAddress(tokenOut) ?? tokenInAddr,
            decayStartTime: BigInt(decayStart),
            decayEndTime: BigInt(decayEnd),
            startAmount: parseUnits(
              startAmount || amountIn,
              TOKEN_DECIMALS[tokenOut] ?? 18,
            ),
            endAmount: parseUnits(
              endAmount || minOutput || "0",
              TOKEN_DECIMALS[tokenOut] ?? 18,
            ),
          },
        });

        toast.info(`Signed order: ${signature.slice(0, 20)}...`);

        orderCounter++;
        const newOrder: DutchOrder = {
          id: `order-${String(orderCounter)}`,
          maker: address,
          inputToken: tokenIn,
          outputToken: tokenOut,
          inputAmount: amountIn,
          minOutput: minOutput || endAmount || "0",
          startAmount: startAmount || amountIn,
          endAmount: endAmount || minOutput || "0",
          decayStartTime: decayStart,
          decayEndTime: decayEnd,
          deadline: orderDeadline,
          reactor,
          signature: signature,
          status: "active",
          createdAt: Date.now(),
        };
        onOrderCreated(newOrder);

        // Dummy tx -- signing doesn't produce a tx
        const hash = await walletClient.sendTransaction({
          chain,
          account: address,
          to: address,
          value: 0n,
        });
        return hash;
      },
    });

    const toastId = toast.loading(
      `Signing order: ${formatTokenDisplay(amountIn, 4).display} ${tokenIn} \u2192 ${tokenOut}...`,
    );
    await flow.execute(
      steps,
      () => {
        toast.dismiss(toastId);
        toast.success(
          `Order signed: ${formatTokenDisplay(amountIn, 4).display} ${tokenIn} \u2192 ${tokenOut}`,
        );
        onTxSuccess();
        resetForm();
      },
      (_label, _err) => {
        onTxError();
      },
    );
    toast.dismiss(toastId);
  }

  const stepperCurrent = flow.steps.filter(
    (s) => s.status === "success" || s.status === "skipped",
  ).length;
  const hasError = flow.steps.some((s) => s.status === "error");
  const errorStep = flow.steps.find((s) => s.status === "error");

  const isFormValid =
    isConnected &&
    tokenIn !== tokenOut &&
    !!amountIn &&
    parseFloat(amountIn) > 0 &&
    !insufficientBalance &&
    !!reactor;

  const confirmSummary = {
    inputAmount: amountIn,
    inputToken: tokenIn,
    outputRange: `${startAmount || amountIn} \u2013 ${endAmount || minOutput || "0"}`,
    outputToken: tokenOut,
    duration: formatDuration(decayDurationNum),
    reactor: REACTOR_DESCRIPTIONS[reactor] ?? reactor,
    deadline: formatDuration(
      decayDurationNum + (parseInt(deadlineMinutes) || 10) * 60,
    ),
  };

  // Decay curve preview data
  const decayCurvePreview = useMemo(() => {
    const minOut = parseFloat(minOutput) || 0;
    const previewStart =
      startAmountNum > 0 ? startAmountNum : minOut > 0 ? minOut * 1.05 : 0;
    const previewEnd = endAmountNum > 0 ? endAmountNum : minOut;
    const isFallback = !(startAmountNum > 0 && endAmountNum > 0) && minOut > 0;
    const canShow = previewStart > 0 && previewEnd > 0 && decayDurationNum > 0;
    return { previewStart, previewEnd, isFallback, canShow };
  }, [startAmountNum, endAmountNum, minOutput, decayDurationNum]);

  return (
    <>
      <PcbCard
        label="AUCTION-01"
        className={cn(
          "mx-auto w-full max-w-[420px] [&_.pcb-card-inner]:p-0",
          feedbackClass,
        )}
        cut={10}
      >
        {!isConnected ? (
          <div className="p-4">
            <Alert variant="warning" title="Wallet Required">
              Connect a wallet to create UniswapX orders.
            </Alert>
          </div>
        ) : (
          <>
            {/* ---- Card header ---- */}
            <div className="flex items-center justify-between px-4 pb-0 pt-4">
              <span className="font-sans text-sm font-medium text-white/90">
                Dutch Auction
              </span>
              <div className="flex items-center gap-2">
                {showHowItWorks ? (
                  <button
                    type="button"
                    onClick={() => {
                      toggleHowItWorks(false);
                    }}
                    className="text-[var(--color-gold)]/60 text-[10px] transition-colors hover:text-[var(--color-gold)]"
                    title="Dismiss"
                  >
                    {"\u2715"} how it works
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      toggleHowItWorks(true);
                    }}
                    className="text-[10px] text-white/30 transition-colors hover:text-white/50"
                  >
                    ? how it works
                  </button>
                )}
              </div>
            </div>

            {/* ---- How it works (compact inline) ---- */}
            <div
              className={cn(
                "grid transition-[grid-template-rows] duration-200 ease-out",
                showHowItWorks ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
            >
              <div className="overflow-hidden">
                <div className="mx-4 mt-2 flex items-center gap-2 rounded-sm bg-[rgba(201,168,76,0.04)] px-3 py-2">
                  <div className="flex-1 text-[11px] leading-relaxed text-white/40">
                    Price starts high, decays over time. Fillers compete for
                    best fill.
                  </div>
                  <div
                    className="h-1 w-16 shrink-0 rounded-full"
                    style={{
                      background: "linear-gradient(90deg, #34d16a, #C9A84C)",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ---- YOU SELL section ---- */}
            <div className="mx-3 mt-3">
              <div className="octa-sm bg-[rgba(255,255,255,0.03)] px-3 py-3">
                <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-white/30">
                  You sell
                </div>
                <div className="flex items-center gap-2">
                  <AmountInput
                    value={amountIn}
                    onChange={setAmountIn}
                    symbol={tokenIn}
                    balance={inputBalance?.raw}
                    decimals={
                      inputBalance?.decimals ?? TOKEN_DECIMALS[tokenIn] ?? 18
                    }
                  />
                  <TokenSelector
                    symbol={tokenIn}
                    onChange={setTokenIn}
                    testId="token-in"
                    tokenAddresses={tokenAddressMap}
                    balances={tokenBalancesForSelector}
                  />
                </div>
                {insufficientBalance && (
                  <div className="mt-1 text-[10px] text-red-400">
                    Insufficient {tokenIn} balance
                  </div>
                )}
              </div>
            </div>

            {/* ---- Swap direction + rate bar ---- */}
            <div className="mx-3 my-1">
              <div className="flex items-center gap-2">
                <TokenIcon symbol={tokenIn} size={18} />
                <div className="flex flex-1 items-center justify-center">
                  {marketRate !== null ? (
                    <span className="font-mono text-[11px] text-white/35">
                      1 {tokenIn} ={" "}
                      {marketRate.toLocaleString(undefined, {
                        maximumFractionDigits: 6,
                      })}{" "}
                      {tokenOut}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 font-mono text-[11px] text-white/15">
                      {tokenIn}
                      <span className="text-[var(--color-gold)]/40">
                        {"\u2192"}
                      </span>
                      {tokenOut}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSwapTokens}
                  className="flex h-6 w-6 items-center justify-center rounded-full border border-[var(--glass-border)] text-[11px] text-white/40 transition-all hover:border-[var(--color-gold-dim)] hover:text-white/70"
                  data-testid="swap-direction"
                  title="Swap direction"
                >
                  {"\u21C5"}
                </button>
                <TokenIcon symbol={tokenOut} size={18} />
              </div>
            </div>

            {/* ---- YOU RECEIVE section ---- */}
            <div className="mx-3">
              <div className="octa-sm bg-[rgba(255,255,255,0.03)] px-3 py-3">
                <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-white/30">
                  Min receive
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Input
                      type="number"
                      placeholder="0.0"
                      value={minOutput}
                      onChange={(e) => {
                        setMinOutput(e.target.value);
                      }}
                    />
                    {minOutputUsd > 0 && (
                      <span className="mt-0.5 block text-[10px] text-white/30">
                        {"\u2248"} {formatUsd(minOutputUsd)}
                      </span>
                    )}
                  </div>
                  <TokenSelector
                    symbol={tokenOut}
                    onChange={setTokenOut}
                    testId="token-out"
                    tokenAddresses={tokenAddressMap}
                    balances={tokenBalancesForSelector}
                  />
                </div>
                {outputBalance && (
                  <div className="mt-1 font-mono text-[10px] text-white/25">
                    Balance:{" "}
                    {
                      formatTokenDisplay(
                        formatUnits(outputBalance.raw, outputBalance.decimals),
                      ).display
                    }{" "}
                    {tokenOut}
                  </div>
                )}
              </div>
            </div>

            {/* ---- Decay range (start → end) ---- */}
            <div className="mx-3 mt-3">
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-white/30">
                Price decay range ({tokenOut})
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="relative">
                    <Input
                      type="number"
                      placeholder={suggestedStart ?? "Best price"}
                      value={startAmount}
                      onChange={(e) => {
                        setStartAmount(e.target.value);
                      }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-semibold text-green-400/70">
                      BEST
                    </span>
                  </div>
                  {!startAmount && suggestedStart && (
                    <button
                      type="button"
                      onClick={() => {
                        setStartAmount(suggestedStart);
                      }}
                      className="mt-0.5 text-[9px] text-white/25 transition-colors hover:text-[var(--color-gold)]"
                    >
                      Use {suggestedStart}
                    </button>
                  )}
                </div>
                <div>
                  <div className="relative">
                    <Input
                      type="number"
                      placeholder={suggestedEnd ?? "Worst price"}
                      value={endAmount}
                      onChange={(e) => {
                        setEndAmount(e.target.value);
                      }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-semibold text-[#C9A84C]/70">
                      WORST
                    </span>
                  </div>
                  {!endAmount && suggestedEnd && (
                    <button
                      type="button"
                      onClick={() => {
                        setEndAmount(suggestedEnd);
                      }}
                      className="mt-0.5 text-[9px] text-white/25 transition-colors hover:text-[var(--color-gold)]"
                    >
                      Use {suggestedEnd}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ---- Duration + Deadline (single row) ---- */}
            <div className="mx-3 mt-3">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-white/30">
                    Decay
                  </div>
                  <div className="flex gap-1">
                    {DURATION_PRESETS.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => {
                          handleDurationPreset(p.value);
                        }}
                        className={cn(
                          "rounded-sm px-2 py-1 font-mono text-[10px] transition-colors",
                          parseInt(decayDuration) === p.value
                            ? "bg-[rgba(201,168,76,0.15)] text-[#C9A84C]"
                            : "bg-white/[0.03] text-white/30 hover:bg-white/[0.06] hover:text-white/50",
                        )}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="w-20">
                  <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-white/30">
                    Expiry
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      placeholder="10"
                      value={deadlineMinutes}
                      onChange={(e) => {
                        setDeadlineMinutes(e.target.value);
                      }}
                    />
                    <span className="text-[10px] text-white/25">m</span>
                  </div>
                </div>
              </div>
              {decayDuration && parseInt(decayDuration) > 0 && (
                <p className="mt-1 text-[10px] text-white/20">
                  Decays over{" "}
                  <span className="font-mono text-white/35">
                    {formatDuration(parseInt(decayDuration))}
                  </span>
                  , then stays at end price for{" "}
                  <span className="font-mono text-white/35">
                    {formatDuration((parseInt(deadlineMinutes) || 10) * 60)}
                  </span>
                </p>
              )}
            </div>

            {/* ---- Live Decay Curve Preview ---- */}
            <div
              className={cn(
                "grid transition-[grid-template-rows] duration-200 ease-out",
                decayCurvePreview.canShow
                  ? "grid-rows-[1fr]"
                  : "grid-rows-[0fr]",
              )}
            >
              <div className="overflow-hidden">
                {decayCurvePreview.canShow && (
                  <div className="mx-3 mt-2 rounded-sm bg-[rgba(255,255,255,0.015)] p-2">
                    <DecayCurve
                      title={`${formatTokenDisplay(String(decayCurvePreview.previewStart), 4).display} \u2192 ${formatTokenDisplay(String(decayCurvePreview.previewEnd), 4).display} ${tokenOut}`}
                      startAmount={decayCurvePreview.previewStart}
                      endAmount={decayCurvePreview.previewEnd}
                      decayDuration={decayDurationNum}
                      height={80}
                      compact
                      outputSymbol={tokenOut}
                    />
                    {decayCurvePreview.isFallback && (
                      <p className="mt-0.5 text-[9px] italic text-white/15">
                        Based on min output
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ---- Market rate comparison ---- */}
            <div
              className={cn(
                "grid transition-[grid-template-rows] duration-200 ease-out",
                effectiveRate !== null && marketRate !== null
                  ? "grid-rows-[1fr]"
                  : "grid-rows-[0fr]",
              )}
            >
              <div className="overflow-hidden">
                {effectiveRate !== null && marketRate !== null && (
                  <div className="mx-3 mt-2 flex items-center gap-2 rounded-sm bg-[rgba(255,255,255,0.02)] px-3 py-1.5 font-mono text-[10px]">
                    <span className="text-white/25">Your rate:</span>
                    <span
                      className={cn(
                        (() => {
                          const deviation =
                            Math.abs(effectiveRate - marketRate) / marketRate;
                          if (deviation > 0.2) return "text-red-400";
                          if (deviation > 0.05) return "text-[#C9A84C]";
                          return "text-white/40";
                        })(),
                      )}
                    >
                      {effectiveRate.toLocaleString(undefined, {
                        maximumFractionDigits: 6,
                      })}{" "}
                      {tokenOut}/{tokenIn}
                    </span>
                    {(() => {
                      const deviation =
                        Math.abs(effectiveRate - marketRate) / marketRate;
                      if (deviation > 0.01) {
                        return (
                          <span className="text-white/20">
                            ({(deviation * 100).toFixed(0)}%{" "}
                            {effectiveRate < marketRate ? "below" : "above"})
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* ---- Reactor (compact) ---- */}
            <div className="mx-3 mt-2">
              <button
                type="button"
                onClick={() => {
                  setShowAdvanced(!showAdvanced);
                }}
                className="flex items-center gap-1.5 text-[10px] text-white/25 transition-colors hover:text-white/40"
              >
                <span
                  className={cn(
                    "inline-block text-[8px] transition-transform",
                    showAdvanced && "rotate-90",
                  )}
                >
                  {"\u25B6"}
                </span>
                Reactor: {REACTOR_DESCRIPTIONS[reactor] ?? reactor}
              </button>
              <div
                className={cn(
                  "grid transition-[grid-template-rows] duration-200 ease-out",
                  showAdvanced ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                )}
              >
                <div className="overflow-hidden">
                  <div className="mt-2">
                    <Select
                      options={reactorOptions}
                      value={reactor}
                      onValueChange={setReactor}
                    />
                    {REACTOR_SUBTITLES[reactor] && (
                      <p className="mt-1 text-[9px] italic text-white/20">
                        {REACTOR_SUBTITLES[reactor]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ---- Stepper ---- */}
            <div
              className={cn(
                "grid transition-[grid-template-rows] duration-200 ease-out",
                flow.steps.length > 0 ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
            >
              <div className="overflow-hidden">
                {flow.steps.length > 0 && (
                  <div className="mx-3 mt-2">
                    <Stepper
                      steps={flow.steps.map((s) => s.label)}
                      currentStep={stepperCurrent}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ---- Errors ---- */}
            <div
              className={cn(
                "grid transition-[grid-template-rows] duration-200 ease-out",
                hasError ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
            >
              <div className="overflow-hidden">
                {hasError && errorStep && (
                  <Alert
                    variant="error"
                    title="Transaction Failed"
                    className="mx-3 mt-2"
                  >
                    {errorStep.error}
                  </Alert>
                )}
              </div>
            </div>

            {tokenIn === tokenOut && (
              <Alert variant="error" title="Invalid pair" className="mx-3 mt-2">
                Input and output tokens must be different.
              </Alert>
            )}

            {/* ---- Submit ---- */}
            <div className="p-3 pt-3">
              <Button
                variant={isFormValid ? "gold" : "outline"}
                size="sm"
                onClick={() => {
                  setShowConfirm(true);
                }}
                disabled={!isFormValid || flow.isExecuting}
                loading={flow.isExecuting}
                className="w-full"
              >
                {flow.isExecuting
                  ? "Signing..."
                  : !reactor
                    ? "No reactors on this chain"
                    : insufficientBalance
                      ? `Insufficient ${tokenIn} balance`
                      : !amountIn || parseFloat(amountIn) <= 0
                        ? "Enter amount"
                        : tokenIn === tokenOut
                          ? "Select different tokens"
                          : !hasDecayParams && !minOutput
                            ? "Enter prices"
                            : "Review & Sign Order"}
              </Button>
              <div className="mt-1.5 text-center text-[9px] text-white/15">
                Free off-chain signing (EIP-712)
              </div>
            </div>
          </>
        )}
      </PcbCard>

      {/* Confirmation Modal */}
      {showConfirm && (
        <Modal
          open={showConfirm}
          onOpenChange={(open) => {
            if (!open) setShowConfirm(false);
          }}
          title="Confirm Dutch Auction Order"
        >
          <div className="space-y-3 text-sm">
            {/* Rate deviation warning */}
            {(() => {
              const startAmt = parseFloat(startAmount || amountIn);
              const inAmt = parseFloat(amountIn);
              if (marketRate && startAmt > 0 && inAmt > 0) {
                const impliedRate = startAmt / inAmt;
                const deviation =
                  Math.abs(impliedRate - marketRate) / marketRate;
                if (deviation > 0.5) {
                  return (
                    <div className="space-y-2 rounded-sm border-2 border-red-400/50 bg-red-400/5 p-3">
                      <Alert variant="error" title="Critical Rate Deviation">
                        Start amount implies a rate{" "}
                        {(deviation * 100).toFixed(0)}% different from market.
                      </Alert>
                      <div className="text-xs text-red-400">
                        Effective rate: 1 {tokenIn} ={" "}
                        {impliedRate.toLocaleString(undefined, {
                          maximumFractionDigits: 6,
                        })}{" "}
                        {tokenOut} ({(deviation * 100).toFixed(0)}%{" "}
                        {impliedRate < marketRate ? "below" : "above"} market
                        rate of{" "}
                        {marketRate.toLocaleString(undefined, {
                          maximumFractionDigits: 6,
                        })}
                        )
                      </div>
                      <Toggle
                        checked={riskAcknowledged}
                        onCheckedChange={setRiskAcknowledged}
                        label="I understand the risk"
                        className="text-xs text-red-400 [&_span]:text-red-400"
                      />
                    </div>
                  );
                }
                if (deviation > 0.2) {
                  return (
                    <Alert variant="warning" title="Rate Deviation">
                      Start amount implies a rate {(deviation * 100).toFixed(0)}
                      % different from market (
                      {marketRate.toLocaleString(undefined, {
                        maximumFractionDigits: 6,
                      })}
                      ).
                    </Alert>
                  );
                }
              }
              return null;
            })()}

            {/* Sell summary */}
            <div className="octa-sm bg-[rgba(255,255,255,0.03)] p-3">
              <div className="text-[10px] text-white/30">You sell</div>
              <div className="mt-1 flex items-center justify-between">
                <span className="font-mono text-lg font-semibold">
                  {confirmSummary.inputAmount} {confirmSummary.inputToken}
                </span>
                <TokenIcon symbol={tokenIn} size={28} />
              </div>
              {inputUsd > 0 && (
                <div className="text-[10px] text-white/25">
                  {formatUsd(inputUsd)}
                </div>
              )}
            </div>

            <div className="flex justify-center text-white/20">{"\u2193"}</div>

            {/* Receive summary */}
            <div className="octa-sm bg-[rgba(255,255,255,0.03)] p-3">
              <div className="text-[10px] text-white/30">You receive</div>
              <div className="mt-1 flex items-center justify-between">
                <span className="font-mono text-lg">
                  {confirmSummary.outputRange} {confirmSummary.outputToken}
                </span>
                <TokenIcon symbol={tokenOut} size={28} />
              </div>
              {(() => {
                const startUsd =
                  (parseFloat(startAmount || amountIn) || 0) *
                  (DEVENV_TOKEN_PRICES[tokenOut] ?? 1);
                const endUsd =
                  (parseFloat(endAmount || minOutput || "0") || 0) *
                  (DEVENV_TOKEN_PRICES[tokenOut] ?? 1);
                if (
                  startUsd > 0 &&
                  endUsd > 0 &&
                  Math.abs(startUsd - endUsd) > 0.01
                ) {
                  return (
                    <div className="text-[10px] text-white/25">
                      ~{formatUsd(Math.max(startUsd, endUsd))} {"\u2013"} ~
                      {formatUsd(Math.min(startUsd, endUsd))}
                    </div>
                  );
                }
                return minOutputUsd > 0 ? (
                  <div className="text-[10px] text-white/25">
                    {"\u2248"} {formatUsd(minOutputUsd)}
                  </div>
                ) : null;
              })()}
            </div>

            {/* Details grid */}
            <div className="space-y-2 rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.02)] p-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-white/30">Decay duration</span>
                <span className="font-mono">{confirmSummary.duration}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/30">Total lifetime</span>
                <div className="text-right">
                  <span className="font-mono">{confirmSummary.deadline}</span>
                  <div className="text-[10px] text-white/20">
                    {confirmSummary.duration} decay +{" "}
                    {formatDuration((parseInt(deadlineMinutes) || 10) * 60)} at
                    end
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/30">Reactor</span>
                <span className="text-[11px]">{confirmSummary.reactor}</span>
              </div>
            </div>

            {/* Mini decay curve */}
            {hasDecayParams && (
              <div className="rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.02)] p-2">
                <DecayCurve
                  startAmount={startAmountNum}
                  endAmount={endAmountNum}
                  decayDuration={decayDurationNum}
                  height={80}
                  compact
                  outputSymbol={tokenOut}
                />
              </div>
            )}

            <div className="text-center text-[10px] text-white/20">
              Signing: Free (off-chain EIP-712)
            </div>

            <div className="flex gap-3 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowConfirm(false);
                }}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                variant="gold"
                size="sm"
                onClick={handleSubmitOrder}
                disabled={hasCriticalDeviation && !riskAcknowledged}
                className="flex-1"
              >
                Sign Order
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
